import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { formatDateOnly } from '../utils/dateUtils';

const GoogleAdsDashboard = ({ accounts, onRefresh }) => {
  const { showNotification } = useNotifications();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadDashboardData();
    
    if (autoRefresh) {
      const interval = setInterval(loadDashboardData, 30000); // Actualizar cada 30 segundos
      return () => clearInterval(interval);
    }
  }, [selectedAccount, autoRefresh]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedAccount !== 'all') {
        const account = accounts.find(acc => acc.id === parseInt(selectedAccount));
        if (account) {
          params.append('customer_id', account.customer_id);
        }
      }
      
      const response = await fetch(`/api/google-ads/dashboard?${params}`);
      
      if (!response.ok) {
        throw new Error('Error al cargar datos del dashboard');
      }
      
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
      showNotification('Error al cargar datos del dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  const syncAllAccounts = async () => {
    try {
      setLoading(true);
      showNotification('Sincronizando todas las cuentas...', 'info');
      
      const response = await fetch('/api/google-ads/sync-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error en la sincronización');
      }
      
      const result = await response.json();
      showNotification(
        `Sincronización completada: ${result.summary.success} exitosas, ${result.summary.errors} errores`,
        result.summary.errors > 0 ? 'warning' : 'success'
      );
      
      // Recargar datos del dashboard
      await loadDashboardData();
      
    } catch (error) {
      console.error('Error sincronizando:', error);
      showNotification('Error en la sincronización masiva', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (value, thresholds) => {
    if (value >= thresholds.good) return 'text-green-600 bg-green-100';
    if (value >= thresholds.warning) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-MX').format(num);
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todas las cuentas</option>
            {accounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.account_name}
              </option>
            ))}
          </select>
          
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Auto-actualizar</span>
          </label>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <span>🔄</span>
            <span>Actualizar</span>
          </button>
          
          <button
            onClick={syncAllAccounts}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <span>⚡</span>
            <span>Sincronizar Todo</span>
          </button>
        </div>
      </div>

      {dashboardData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Campañas Activas</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(dashboardData.summary.total_campaigns)}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Gasto Total</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(dashboardData.summary.total_cost)}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <span className="text-2xl">💰</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Clics Totales</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(dashboardData.summary.total_clicks)}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <span className="text-2xl">👆</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversiones</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(dashboardData.summary.total_conversions)}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <span className="text-2xl">🎯</span>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">CTR Promedio</h3>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                getStatusColor(dashboardData.summary.average_ctr, { good: 2, warning: 1 })
              }`}>
                {(dashboardData.summary.average_ctr).toFixed(2)}%
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">CPC Promedio</h3>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                getStatusColor(1 / (dashboardData.summary.average_cpc || 1), { good: 2, warning: 1 })
              }`}>
                {formatCurrency(dashboardData.summary.average_cpc)}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Alertas Activas</h3>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                dashboardData.summary.active_alerts > 0 ? 'text-red-600 bg-red-100' : 'text-green-600 bg-green-100'
              }`}>
                {dashboardData.summary.active_alerts} alerta{dashboardData.summary.active_alerts !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Recent Alerts */}
          {dashboardData.recent_alerts && dashboardData.recent_alerts.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">🔔 Alertas Recientes</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {dashboardData.recent_alerts.map((alert, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <span className="text-red-600 text-sm">⚠️</span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-red-900">
                          {alert.alert_name} - {alert.account_name}
                        </p>
                        <p className="text-sm text-red-700">
                          {alert.alert_type}: {alert.message}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          Raw triggered_at: {alert.triggered_at} <br/>
                          Formatted: {formatDateOnly(alert.triggered_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Campaign Performance Table */}
          {dashboardData.metrics && dashboardData.metrics.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">📈 Rendimiento de Campañas (Hoy)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Campaña
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Impresiones
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clics
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CTR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CPC
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gasto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conversiones
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ROAS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.metrics.slice(0, 10).map((metric, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {metric.campaign_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {metric.account_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(metric.impressions)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(metric.clicks)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getStatusColor(metric.ctr, { good: 2, warning: 1 })
                          }`}>
                            {metric.ctr.toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(metric.cpc_micros / 1000000)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(metric.cost_micros / 1000000)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(metric.conversions)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getStatusColor(metric.calculated_roas, { good: 4, warning: 2 })
                          }`}>
                            {metric.calculated_roas.toFixed(2)}x
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Last Updated */}
          <div className="text-center text-sm text-gray-500">
            Raw last_updated: {dashboardData.last_updated} <br/>
            Última actualización: {formatDateOnly(dashboardData.last_updated)}
          </div>
        </>
      )}

      {!dashboardData && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos disponibles</h3>
          <p className="text-gray-500 mb-4">
            Configura y conecta tus cuentas de Google Ads para ver las métricas en tiempo real.
          </p>
          <button
            onClick={() => onRefresh()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Configurar Cuentas
          </button>
        </div>
      )}
    </div>
  );
};

export default GoogleAdsDashboard;
